import { useEffect, useMemo, useRef, useState } from 'react';
import { queryClient } from '@/app/providers/query-client';
import { aiJobsBatchStatusKeys, useAIJobsBatchStatus } from '@/hooks/api/useAIJobsBatchStatus';
import { invalidateCanvasHistoryQueriesForAsset } from '@/hooks/api/useCanvasSnapshot';
import { useToast } from '@/hooks/use-toast';
import { normalizeCanvasGenerationStatus } from '@/lib/canvas/canvas-generation-job-status';
import { pinEditedFootageOnOpenCanvas } from '@/lib/canvas/canvas-footage-edit-pin';
import { pinEditedReferenceOnOpenCanvas } from '@/lib/canvas/canvas-reference-edit-pin';
import type { ImageEditJobResult } from '@/types/ai';
import type {
  EditImageSaveResult,
  EditImageSaveTarget,
  ImageEditPinIntent,
} from '@/types/edit-image';
import { useEditorStore } from '@/stores/editorStore';
import { useUIStore } from '@/stores/uiStore';
import { useAppStore } from '@/stores/appStore';
import { useOrgContextStore } from '@/stores/orgContextStore';
import {
  IMAGE_EDIT_TASK_CHANGE_EVENT,
  readTrackedImageEditTasks,
  getImageEditTaskScope,
  dismissLegacyImageEditTask,
  updateTrackedImageEditTask,
  removeTrackedImageEditTask,
  withImageEditTaskLock,
  IMAGE_EDIT_PRESENTATION_TTL,
  type TrackedImageEditTask,
} from '@/lib/image-edit-task-tracker';

const MAX_IMAGE_EDIT_RESULT_RECOVERY_RETRIES = 3;
const MAX_STORYBOARD_RESULT_VISIBILITY_ATTEMPTS = 3;
const MAX_MISSING_STATUS_RESPONSES = 3;

type ImageEditCompletionAction = 'remove' | 'retry-status' | 'wait-presentation';
type ImageEditCompletion = {
  action: ImageEditCompletionAction;
  updatedCurrentResult: boolean;
  placementFailure?: string;
};

function toSaveResult(result: ImageEditJobResult): EditImageSaveResult {
  if (result.kind === 'storyboard-image') {
    return { kind: result.kind, resourceId: result.resourceId, planId: result.planId };
  }
  if (result.kind === 'project-resource') {
    return { kind: result.kind, resourceId: result.resourceId };
  }
  if (result.kind === 'footage-file') {
    return {
      kind: result.kind,
      resourceId: result.resourceId,
      name: result.name,
      fileUrl: result.url,
      mimeType: result.mimeType,
      width: result.width,
      height: result.height,
      mediaKind: 'image',
      editAction: result.editAction,
    };
  }
  return {
    kind: result.kind,
    attachmentId: result.attachmentId || result.resourceId,
    fileUrl: result.url,
    name: result.name,
    mimeType: result.mimeType,
    width: result.width,
    height: result.height,
    editAction: result.editAction,
  };
}

async function invalidateImageEditPresentation(
  target: EditImageSaveTarget,
  result: ImageEditJobResult
): Promise<void> {
  if (target.kind === 'storyboard-image') {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['storyboard-images', target.storyboardId] }),
      queryClient.invalidateQueries({ queryKey: ['storyboard-plans'] }),
      queryClient.invalidateQueries({ queryKey: ['storyboards'] }),
    ]);
  } else if (target.kind === 'project-resource') {
    const projectIds = Array.from(
      new Set(
        [
          target.projectId,
          target.permissionProjectId,
          result.projectId,
          result.contextProjectId,
        ].filter(Boolean) as string[]
      )
    );
    const assetId = result.elementId ?? result.element_id;
    const elementType = result.elementType ?? result.element_type;
    for (const projectId of projectIds) {
      if (assetId) {
        invalidateCanvasHistoryQueriesForAsset(queryClient, {
          projectId,
          assetId,
          assetType: elementType === 'location' ? 'location' : elementType,
          includeSnapshot: false,
        });
      } else {
        // 旧任务没有资产身份，只匹配已有的同项目/标签历史，绝不重拉快照。
        void queryClient.invalidateQueries({
          predicate: (query) => {
            const key = query.queryKey;
            if (
              key[0] !== 'canvas-snapshot' ||
              key[1] !== projectId ||
              key[2] !== 'asset' ||
              key[6] !== 'history'
            )
              return false;
            const targetKey = key[7];
            return typeof targetKey === 'string' && targetKey.split(':')[3] === target.elementTagId;
          },
        });
      }
    }
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['resource-library', 'v1', 'list'] }),
      queryClient.invalidateQueries({ queryKey: ['resource-library', 'v1', 'infinite'] }),
      ...projectIds.map((projectId) =>
        queryClient.invalidateQueries({
          queryKey: ['resource-library', 'v1', 'tag-resources', projectId, target.elementTagId],
        })
      ),
      queryClient.invalidateQueries({ queryKey: ['project-element-tags'] }),
      queryClient.invalidateQueries({ queryKey: ['storyboard-items'] }),
    ]);
  } else if (target.kind === 'footage-file') {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['resource-library', 'v1'] }),
      queryClient.invalidateQueries({ queryKey: ['project-resources'] }),
      queryClient.invalidateQueries({ queryKey: ['storyboard-items'] }),
    ]);
  } else {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['attachments'] }),
      queryClient.invalidateQueries({ queryKey: ['canvas-layout'] }),
      queryClient.invalidateQueries({ queryKey: ['canvas-items'] }),
    ]);
  }
}

function isImageEditResultForTarget(
  result: ImageEditJobResult | undefined,
  target: EditImageSaveTarget
): result is ImageEditJobResult {
  if (!result?.resourceId || result.kind !== target.kind) return false;
  if (target.kind === 'storyboard-image')
    return result.storyboardId === target.storyboardId && result.planId === target.planId;
  if (target.kind === 'project-resource') return result.elementTagId === target.elementTagId;
  if (target.kind === 'markup-reference')
    return result.projectId === target.projectId && result.storyboardId === target.storyboardId;
  return (
    result.contextProjectId === (target.permissionProjectId ?? target.projectId) ||
    result.projectId === target.projectId
  );
}

function isStoryboardResultVisibleInActiveQueries(
  storyboardId: string,
  planId: string,
  resourceId: string
): boolean {
  return queryClient
    .getQueryCache()
    .findAll({ queryKey: ['storyboard-images', storyboardId] })
    .some((query) => {
      if (query.getObserversCount() === 0) return false;
      const queryPlanId = query.queryKey[2];
      if (queryPlanId !== planId && queryPlanId !== '__all__') return false;
      const data: unknown = query.state.data;
      return (
        Array.isArray(data) &&
        data.some(
          (item) =>
            typeof item === 'object' &&
            item !== null &&
            'imageId' in item &&
            item.imageId === resourceId
        )
      );
    });
}

async function waitForStoryboardResultVisibility(
  target: Extract<EditImageSaveTarget, { kind: 'storyboard-image' }>,
  resourceId: string
): Promise<void> {
  if (isStoryboardResultVisibleInActiveQueries(target.storyboardId, target.planId, resourceId)) {
    return;
  }

  for (let attempt = 1; attempt < MAX_STORYBOARD_RESULT_VISIBILITY_ATTEMPTS; attempt += 1) {
    await queryClient.refetchQueries({
      queryKey: ['storyboard-images', target.storyboardId],
      type: 'active',
    });
    if (isStoryboardResultVisibleInActiveQueries(target.storyboardId, target.planId, resourceId)) {
      return;
    }
  }
}

async function presentCompletedImageEdit(
  target: EditImageSaveTarget,
  saveResult: EditImageSaveResult,
  task: TrackedImageEditTask,
  isCurrent: () => boolean
): Promise<ImageEditCompletion> {
  const makeIntent = (): ImageEditPinIntent | null => {
    const canvas =
      target.kind === 'footage-file'
        ? target.canvasContext
        : target.kind === 'markup-reference'
          ? {
              projectId: target.projectId,
              contextType: 'storyboard' as const,
              storyboardId: target.storyboardId,
            }
          : undefined;
    if (!canvas || !('sourceNodeId' in target) || !target.sourceNodeId || !target.sourceResourceId)
      return null;
    return {
      jobId: task.jobId,
      canvas,
      sourceNodeId: target.sourceNodeId,
      sourceResourceId: target.sourceResourceId,
      isCurrent,
      onNodeReserved: (nodeId) =>
        updateTrackedImageEditTask(task.scope, task.jobId, { canvasNodeId: nodeId }),
    };
  };
  if (target.kind === 'storyboard-image' && saveResult.kind === 'storyboard-image') {
    const isStillViewingTarget = () => {
      const editorState = useEditorStore.getState();
      return (
        isCurrent() &&
        editorState.selectedStoryboardId === target.storyboardId &&
        editorState.editorContext.mode === 'storyboard-image' &&
        editorState.editorContext.tagId === target.planId
      );
    };
    if (!isStillViewingTarget()) {
      return { action: 'remove', updatedCurrentResult: false };
    }

    await waitForStoryboardResultVisibility(target, saveResult.resourceId);
    if (!isStillViewingTarget()) {
      return { action: 'remove', updatedCurrentResult: false };
    }
    useEditorStore.getState().selectTag(saveResult.planId ?? target.planId);
    useEditorStore.getState().setEditorContext({
      tagId: saveResult.planId ?? target.planId,
      previewItemId: saveResult.resourceId,
    });
    return { action: 'remove', updatedCurrentResult: true };
  }

  if (target.kind === 'project-resource' && saveResult.kind === 'project-resource') {
    const editorState = useEditorStore.getState();
    return {
      action: 'remove',
      updatedCurrentResult: editorState.selectedTagId === target.elementTagId,
    };
  }

  if (target.kind === 'footage-file' && saveResult.kind === 'footage-file') {
    if (target.pinOnCanvas && target.sourceResourceId) {
      const intent = makeIntent();
      if (!intent)
        return {
          action: 'remove',
          updatedCurrentResult: false,
          placementFailure: '缺少提交时的画布信息，请从文件列表手动拖入',
        };
      const outcome = await pinEditedFootageOnOpenCanvas(
        saveResult,
        target.sourceResourceId,
        intent
      );
      return {
        action: outcome.status === 'not_ready' ? 'wait-presentation' : 'remove',
        updatedCurrentResult: false,
        ...(outcome.status === 'cannot_place' ? { placementFailure: outcome.reason } : {}),
      };
    }

    const editorState = useEditorStore.getState();
    const uiState = useUIStore.getState();
    const isStillViewingSourceFootage =
      isCurrent() &&
      Boolean(target.sourceResourceId) &&
      editorState.selectedResourceId === target.sourceResourceId &&
      editorState.selectedResourceType === 'footage' &&
      uiState.resourcesPanelOpen &&
      uiState.resourcePanelActiveTab === 'footage';
    if (isStillViewingSourceFootage) {
      editorState.selectResource(saveResult.resourceId, 'footage');
      editorState.setEditorMode('storyboard-image');
      uiState.setResourcesPanelOpen(true);
      uiState.setResourcePanelActiveTab('footage');
    }
    return { action: 'remove', updatedCurrentResult: isStillViewingSourceFootage };
  }

  if (target.kind === 'markup-reference' && saveResult.kind === 'markup-reference') {
    const intent = makeIntent();
    if (!intent)
      return {
        action: 'remove',
        updatedCurrentResult: false,
        placementFailure: '缺少提交时的画布信息',
      };
    const outcome = await pinEditedReferenceOnOpenCanvas(saveResult, target.sourceNodeId, intent);
    return {
      action: outcome.status === 'not_ready' ? 'wait-presentation' : 'remove',
      updatedCurrentResult: false,
      ...(outcome.status === 'cannot_place' ? { placementFailure: outcome.reason } : {}),
    };
  }

  return { action: 'remove', updatedCurrentResult: false };
}

/**
 * 根级任务协调器：跨弹窗关闭和页面刷新继续轮询；业务画布的生成中覆盖层仍由各画布原状态负责。
 */
export function ImageEditTaskCoordinator() {
  const { toast } = useToast();
  const userId = useAppStore((state) => state.user?.userId);
  const orgId = useOrgContextStore((state) => state.activeOrgId);
  const scope = userId
    ? encodeURIComponent(userId) + ':' + encodeURIComponent(orgId ?? '__personal__')
    : null;
  const [tasks, setTasks] = useState<TrackedImageEditTask[]>(() => readTrackedImageEditTasks());
  const [presentationRetryNonce, setPresentationRetryNonce] = useState(0);
  const handledRef = useRef(new Set<string>());
  const jobIds = useMemo(
    () => tasks.filter((task) => task.scope === scope).map((task) => task.jobId),
    [tasks, scope]
  );
  const statusQueryKey = JSON.stringify(aiJobsBatchStatusKeys.batch(orgId, jobIds));
  const {
    data: statusData,
    dataUpdatedAt,
    isSuccess,
    isError,
    refetch: refetchStatuses,
  } = useAIJobsBatchStatus(jobIds);
  const [successfulBatch, setSuccessfulBatch] = useState<{
    scope: string;
    queryKey: string;
    updatedAt: number;
  }>();

  useEffect(() => {
    // 只记录现有 batch-status 的新网络成功响应；缓存回放、手动写缓存和切组织不算一次。
    return queryClient.getQueryCache().subscribe((event) => {
      if (
        !scope ||
        getImageEditTaskScope() !== scope ||
        event.type !== 'updated' ||
        event.action.type !== 'success' ||
        event.action.manual ||
        JSON.stringify(event.query.queryKey) !== statusQueryKey
      )
        return;
      setSuccessfulBatch({
        scope,
        queryKey: statusQueryKey,
        updatedAt: event.query.state.dataUpdatedAt,
      });
    });
  }, [scope, statusQueryKey]);

  useEffect(() => {
    const sync = () => setTasks(readTrackedImageEditTasks(scope));
    sync();
    window.addEventListener(IMAGE_EDIT_TASK_CHANGE_EVENT, sync);
    window.addEventListener('storage', sync);
    const ready = () => setPresentationRetryNonce((value) => value + 1);
    window.addEventListener('jointo:image-edit-presentation-ready', ready);
    window.addEventListener('focus', ready);
    return () => {
      window.removeEventListener(IMAGE_EDIT_TASK_CHANGE_EVENT, sync);
      window.removeEventListener('storage', sync);
      window.removeEventListener('jointo:image-edit-presentation-ready', ready);
      window.removeEventListener('focus', ready);
    };
  }, [scope]);

  // 只安排到期唤醒，不建立第二套任务轮询；运行中任务没有 completedAt。
  useEffect(() => {
    const deadlines = tasks
      .filter((task) => task.scope === scope && task.completedAt !== undefined)
      .map((task) => task.completedAt! + IMAGE_EDIT_PRESENTATION_TTL);
    if (!deadlines.length) return;
    const timeout = window.setTimeout(
      () => setPresentationRetryNonce((value) => value + 1),
      Math.max(0, Math.min(...deadlines) - Date.now())
    );
    return () => window.clearTimeout(timeout);
  }, [tasks, scope]);

  useEffect(() => {
    if (!scope) return;
    for (const candidate of tasks) {
      if (candidate.scope !== scope) continue;
      const key = scope + ':' + candidate.jobId;
      if (handledRef.current.has(key)) continue;
      const payload = statusData?.[candidate.jobId];
      if (
        candidate.legacy &&
        statusData &&
        (!payload || !('activeOrgId' in payload) || payload.activeOrgId !== orgId)
      ) {
        dismissLegacyImageEditTask(scope, candidate.jobId);
        continue;
      }
      if (candidate.legacy && payload) {
        handledRef.current.add(key);
        void withImageEditTaskLock(scope, candidate.jobId, async () => {
          if (getImageEditTaskScope() !== scope) return;
          const current = readTrackedImageEditTasks(scope).find(
            (item) => item.jobId === candidate.jobId
          );
          if (!current?.legacy) return;
          updateTrackedImageEditTask(scope, candidate.jobId, { legacy: false });
          dismissLegacyImageEditTask(scope, candidate.jobId, true);
        }).finally(() => {
          handledRef.current.delete(key);
          setPresentationRetryNonce((value) => value + 1);
        });
        continue;
      }
      const status = normalizeCanvasGenerationStatus(payload?.status);
      const hasNewStatusResponse =
        isSuccess &&
        !isError &&
        successfulBatch?.scope === scope &&
        successfulBatch.queryKey === statusQueryKey &&
        successfulBatch.updatedAt === dataUpdatedAt &&
        dataUpdatedAt >= candidate.createdAt &&
        dataUpdatedAt > candidate.lastStatusResponseAt;
      if (status !== 'completed' && status !== 'failed' && !hasNewStatusResponse) continue;
      handledRef.current.add(key);
      let retryStatus = false;
      void withImageEditTaskLock(scope, candidate.jobId, async () => {
        const isCurrent = () => getImageEditTaskScope() === scope;
        if (!isCurrent()) return;
        let task = readTrackedImageEditTasks(scope).find((item) => item.jobId === candidate.jobId);
        if (!task) return; // 另一标签已处理完；不能拿旧闭包重新钉图。
        if (task.legacy) {
          updateTrackedImageEditTask(scope, task.jobId, { legacy: false });
          dismissLegacyImageEditTask(scope, task.jobId, true);
          task = { ...task, legacy: false };
        }
        const patch = (value: Partial<TrackedImageEditTask>) => {
          task = { ...task!, ...value };
          updateTrackedImageEditTask(scope, candidate.jobId, value);
        };
        const notify = (
          kind: 'saved' | 'final',
          title: string,
          description: string,
          failed = false
        ) => {
          if (!task || task.notified[kind] || !isCurrent()) return;
          patch({ notified: { ...task.notified, [kind]: true } });
          toast({ title, description, ...(failed ? { variant: 'destructive' as const } : {}) });
        };
        const finish = () => removeTrackedImageEditTask(scope, candidate.jobId);
        // 在跨标签锁内读取最新计数和批次戳，同一响应因重渲染不能重复累计。
        if (hasNewStatusResponse && dataUpdatedAt > task.lastStatusResponseAt) {
          const missingStatusCount = payload ? 0 : task.missingStatusCount + 1;
          patch({ lastStatusResponseAt: dataUpdatedAt, missingStatusCount });
          if (!payload) {
            if (missingStatusCount >= MAX_MISSING_STATUS_RESPONSES) {
              notify(
                'final',
                '无法获取任务状态',
                '请到生成记录查看；这里只结束界面跟踪，不取消后台任务',
                true
              );
              finish();
            }
            return;
          }
        }
        if (status !== 'completed' && status !== 'failed') return;
        if (status === 'failed') {
          notify(
            'final',
            '图片编辑失败',
            payload?.errorMessage || payload?.error || '请稍后重试',
            true
          );
          finish();
          return;
        }
        if (task.completedAt === undefined) {
          const completed = payload?.completedAt ? Date.parse(payload.completedAt) : NaN;
          // 旧任务无后端时间直接视为可到期，绝不用打开页面的时刻延长期限。
          patch({ completedAt: Number.isFinite(completed) ? completed : 0 });
        }
        const result = payload?.outputData?.imageEditResult;
        if (!isImageEditResultForTarget(result, task.target)) {
          if (task.retryCount < MAX_IMAGE_EDIT_RESULT_RECOVERY_RETRIES) {
            patch({ retryCount: task.retryCount + 1 });
            retryStatus = true;
          } else {
            notify(
              'final',
              '图片已生成，但界面同步失败',
              '未能获取新图片的资源身份，请重新进入对应位置查看',
              true
            );
            finish();
          }
          return;
        }
        try {
          await invalidateImageEditPresentation(task.target, result);
          if (!isCurrent()) return;
          const needsPin =
            task.target.kind === 'markup-reference' ||
            (task.target.kind === 'footage-file' && task.target.pinOnCanvas);
          if (needsPin && Date.now() >= (task.completedAt ?? 0) + IMAGE_EDIT_PRESENTATION_TTL) {
            notify(
              'final',
              '图片已保存，但未放入画布',
              '待放置已超过 24 小时，请从历史或文件列表查看新图片',
              true
            );
            finish();
            return;
          }
          const completion = await presentCompletedImageEdit(
            task.target,
            toSaveResult(result),
            task,
            isCurrent
          );
          if (!isCurrent()) return;
          if (completion.placementFailure) {
            notify('final', '图片已保存，但未放入画布', completion.placementFailure, true);
          } else {
            notify(
              'saved',
              '图片编辑完成',
              completion.updatedCurrentResult ? '新图片已保存并更新为当前结果' : '新图片已保存'
            );
          }
          if (completion.action === 'remove') finish();
        } catch (error) {
          if (!isCurrent()) return;
          if (task.presentationRetryCount < MAX_IMAGE_EDIT_RESULT_RECOVERY_RETRIES) {
            patch({ presentationRetryCount: task.presentationRetryCount + 1 });
            retryStatus = true;
          } else {
            notify(
              'final',
              '图片已生成，但界面同步失败',
              error instanceof Error ? error.message : '请重新进入对应位置查看新图片',
              true
            );
            finish();
          }
        }
      })
        .catch(() => {
          // 存储/互斥失败不改变后端任务；下次 ready/focus 或已有批量状态更新时恢复。
        })
        .finally(() => {
          handledRef.current.delete(key);
          if (retryStatus && getImageEditTaskScope() === scope) {
            void refetchStatuses().finally(() => setPresentationRetryNonce((value) => value + 1));
          }
        });
    }
  }, [
    presentationRetryNonce,
    refetchStatuses,
    scope,
    orgId,
    statusData,
    tasks,
    toast,
    successfulBatch,
    statusQueryKey,
    isSuccess,
    isError,
    dataUpdatedAt,
  ]);

  return null;
}
